/**
 * Who to call about a competition, a series or an event — and what stands in
 * when nobody was named.
 *
 * The concept: `organizerContact` is a name, an e-mail address and a phone
 * number on the competition / series / event document. It is a PERSON OR AN
 * OFFICE THE VISITOR CAN REACH, not a Sendly account and not an access grant.
 * It is deliberately NOT the competition's `organizer` ROLE (gym members with
 * write access to the competition, `isCompOrganizer`) nor the event's
 * `organizers[]` / `organizerUids[]` array — those are permissions, this is
 * contact information, and the named person need not have an account at all.
 * The Hungarian label is therefore "Rendező elérhetősége" everywhere, never the
 * bare "Szervező", which the staff role already owns.
 *
 * Why this lives in the shared package: three document shapes carry the field,
 * and the fallback chain below is exactly the kind of rule that two surfaces
 * implement slightly differently — the same reason `getGroupEntryFee` exists.
 * Pure: no `firebase*`, no `react`, no Node builtin.
 */

/**
 * The per-field caps. ONE number, TWO homes: the editor's `maxLength` and the
 * `organizerContactWriteOk()` helper in `firestore.rules` — the documents this
 * field sits on are world-readable, so a client `maxLength` is not a gate.
 */
export const ORGANIZER_CONTACT_LIMITS = Object.freeze({ name: 120, email: 254, phone: 40 });

/** One field: coerced to string, trimmed, capped. */
function clean(value, max) {
  if (value == null) return "";
  const s = typeof value === "string" ? value : String(value);
  return s.trim().slice(0, max);
}

/**
 * Editor shape → document shape. Returns `null` when all three are empty,
 * because an empty map means the same thing as no map and every reader would
 * otherwise have to unpack it to find that out.
 *
 * A partial contact is valid: a name and a phone with no e-mail is a perfectly
 * good "call the organiser". The unset field is an empty string rather than a
 * missing key, which keeps the rules' `hasOnly(['name','email','phone'])`
 * allowlist simple.
 *
 * Truncating rather than refusing is deliberate: the caps exist so the rules
 * never reject a save the editor allowed, and the editor's `maxLength` already
 * stops a human from getting here.
 */
export function buildOrganizerContact(input) {
  const d = input || {};
  const name = clean(d.name, ORGANIZER_CONTACT_LIMITS.name);
  const email = clean(d.email, ORGANIZER_CONTACT_LIMITS.email);
  const phone = clean(d.phone, ORGANIZER_CONTACT_LIMITS.phone);
  if (!name && !email && !phone) return null;
  return { name, email, phone };
}

/** Is there anything in it? What the editor asks before showing the fallback preview. */
export function hasOrganizerContact(contact) {
  return buildOrganizerContact(contact) !== null;
}

/**
 * What a surface prints: `{ name, email, phone, source }` or `null`.
 *
 * `doc` is the competition / series / event document, `gym` the gym document
 * (`{ name, contactEmail, contactPhone }`). The chain:
 *
 *   1. anything on the document's own `organizerContact` → that, `source: "own"`;
 *   2. otherwise, ONLY with `fallback: true` → the GYM's contact,
 *      `source: "gym"`;
 *   3. otherwise `null`.
 *
 * Step 2 only produces a block when the gym has an e-mail OR a phone: a block
 * carrying nothing but the gym's name is noise, not information.
 *
 * `fallback: true` on every competition and series call site — there the
 * contact is mandatory in spirit, so something must always be visible.
 * `fallback: false` on events, where the field is optional and an empty one
 * shows no block. That difference is the only thing "optional vs mandatory"
 * means in practice, so it has to be visible somewhere.
 *
 * ⚠️ ONE CARVE-OUT, and it overrides `fallback: true`: an EXTERNAL competition
 * (`type: "external"`) never falls back to the gym. Such a competition is
 * somebody else's event that the gym merely lists — a federation round, another
 * wall's open — so printing THIS gym's e-mail and phone as "the contact" would
 * send every question to people who do not run it and cannot answer it. Wrong
 * information is worse than none, so an external competition with no contact of
 * its own shows no block at all. The rule lives here rather than at the six
 * competition call sites for the usual reason: six copies of it would be six
 * chances to disagree.
 */
export function resolveOrganizerContact(doc, gym, { fallback = false } = {}) {
  const own = buildOrganizerContact(doc && doc.organizerContact);
  if (own) return { ...own, source: "own" };
  if (!fallback || (doc && doc.type === "external")) return null;
  const g = gym || {};
  const gymContact = buildOrganizerContact({ name: g.name, email: g.contactEmail, phone: g.contactPhone });
  if (!gymContact || (!gymContact.email && !gymContact.phone)) return null;
  return { ...gymContact, source: "gym" };
}
